import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useAxios, APIError, setCurrentLanguage, getCurrentLanguage } from '../../core/_axios';
import { Service } from '../../core/_models';
import { getAnonServiceByUniqueIdentifier } from '../../core/_requests';
import useLocalizedCopy from '../../hooks/useLocalizedCopy';

const ServiceContext = createContext<Service | null>(null);

export const useService = () => {
    const service = useContext(ServiceContext);
    return { service };
};

export const ServiceProvider = ({ service, children }: { service: Service, children: ReactNode }) => {
    return <ServiceContext.Provider value={service}>{children}</ServiceContext.Provider>;
};

const AnonSignLayout = () => {
    const {
        serviceUniqueId
    } = useParams();

    const axios = useAxios();
    const { copy } = useLocalizedCopy();

    const [loading, setLoading] = useState(true);
    const [loadingError, setLoadingError] = useState('')

    const [service, setService] = useState<Service | null>();

    const loadService = useCallback(async () => {
        if (!serviceUniqueId) {
            setLoading(false);
            setLoadingError(copy.Layout.serviceRequired);
            return;
        }
        setLoading(true);
        setLoadingError(null);
        try {
            const updatedService = await getAnonServiceByUniqueIdentifier(axios, serviceUniqueId);
            setService(updatedService);
        } catch (exception) {
            console.error(exception)
            const errorMessage = exception instanceof APIError ? exception.message : copy.Errors.generic
            setLoadingError(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [axios, serviceUniqueId])

    useEffect(() => {
        loadService();
    }, [loadService])
    
    // Set language based on service language after service is loaded
    useEffect(() => {
        if (service && !loading) {
            const serviceLanguage = service.public_data?.current_language || 'en-US';
            const currentLanguage = getCurrentLanguage();
            
            // Only update language if it's different
            if (serviceLanguage !== currentLanguage) {
                setCurrentLanguage(serviceLanguage);
                // Don't reload - let the app re-render with new language
            }
        }
    }, [service, loading])

    if (loading) {
        return (
            <LoadingSpinner fullScreen />
        );
    }

    if (loadingError || !service) {
        return (
            <div className="container-fixed mt-5">
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                    <span className="block sm:inline">
                        {loadingError || copy.Layout.unableToLoadService}
                    </span>
                </div>
                <div className="mt-5">
                    <button className="btn btn-lg btn-light" onClick={loadService}>
                        {copy.Layout.retry}
                    </button>
                </div>
            </div>
        );
    }

    return <ServiceProvider service={service}>
        <div className="container-fixed">
            <Outlet />
        </div>
    </ServiceProvider>
};

export default AnonSignLayout;
